import type { DocNode } from '@atlaskit/adf-schema';
import { JSONTransformer, type JSONDocNode } from '@atlaskit/editor-json-transformer';
import type { NodeType, Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { Selection, Transaction } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { convertProsemirrorNodeToMarkdown } from '@atlassian/ai-model-io/convert-prosemirror-to-markdown';

import type { EndSessionMetadata } from '../editor-actions/types';
import { aiSessionContextPluginKey } from '../pm-plugins/ai-session-context/ai-session-context-plugin-key';
import {
	createEndAIExperienceCommand,
	createOpenAIModalCommand,
	createStageInlineRovoStreamingCommand,
	createStartAISessionCommand,
	createUpdateTargetIdCommand,
} from '../pm-plugins/decoration/actions';
import { createCommand, getPluginState } from '../pm-plugins/decoration/decoration-plugin-factory';
import type { AIGlobalOptIn } from '../types/types';
import type { EditorPluginAIConfigItem } from '../ui/prebuilt/config-items/config-items';
import type { InvokedFrom } from '../ui/utils/analytics/analyticsFlowTypes';

export const openAIModal = ({
	invokedFrom,
	aiOptIn,
	configItem,
	editorView,
	triggeredFor,
	isExternal = false,
	withAcceptReject = true,
	withStreamingHighlight = true,
	hideLoadingScreen = false,
	traceId,
	singleInstrumentationId,
	isCreate,
	promptVersion,
	userADFInput,
}: {
	aiOptIn: AIGlobalOptIn;
	configItem: EditorPluginAIConfigItem;
	editorView?: EditorView;
	hideLoadingScreen?: boolean;
	invokedFrom: InvokedFrom;
	isCreate?: boolean;
	isExternal?: boolean;
	promptVersion?: string;
	singleInstrumentationId?: string;
	traceId?: string;
	triggeredFor?: NodeType;
	userADFInput?: JSONDocNode;
	withAcceptReject?: boolean;
	withStreamingHighlight?: boolean;
}): void => {
	if (!editorView) {
		return;
	}

	const openAIModalCommand = createOpenAIModalCommand({
		state: editorView.state,
		configItem,
		lastTriggeredFrom: invokedFrom,
		aiGlobalOptIn: aiOptIn,
		triggeredFor,
		isExternal,
		withAcceptReject,
		withStreamingHighlight,
		hideLoadingScreen,
		traceId,
		singleInstrumentationId,
		isCreate,
		promptVersion,
		userADFInput,
	});

	openAIModalCommand(editorView.state, editorView.dispatch);
};

export const endAIExperience = ({ editorView }: { editorView?: EditorView }): void => {
	if (!editorView) {
		return;
	}

	const endAIExperienceCommand = createEndAIExperienceCommand();
	endAIExperienceCommand(editorView.state, editorView.dispatch);
};

export const startAISession = ({
	editorView,
	sessionId,
	traceId,
	promptVersion,
}: {
	editorView?: EditorView;
	promptVersion?: string;
	sessionId: string;
	traceId?: string;
}): boolean | undefined => {
	if (!editorView) {
		return;
	}

	return createStartAISessionCommand(
		sessionId,
		fg('platform_editor_ai_traceid_in_analytics_events') ? traceId : undefined,
		fg('platform_editor_ai_promptversion_in_analytics') ? promptVersion : undefined,
	)(editorView.state, editorView.dispatch);
};

export const endAISession = ({
	editorView,
	sessionId,
	metadata,
}: {
	editorView?: EditorView;
	metadata: EndSessionMetadata;
	sessionId: string;
}): boolean | undefined => {
	if (!editorView) {
		return;
	}

	const endSessionCommand = createCommand(
		(state) => {
			const aiDecorationExperiencePluginState = getPluginState(state);
			const nextSessionId = aiDecorationExperiencePluginState.nextSessionId;

			// If a sessions which is not the next session is trying to be ended then we should abort trying to end the session.
			if (sessionId !== nextSessionId) {
				return false;
			}

			return {
				type: 'END_SESSION',
				data: { sessionId: undefined, type: metadata?.type },
			};
		},
		(tr) => {
			if (expValEquals('platform_editor_ai_xstate_migration', 'isEnabled', true)) {
				tr.setMeta(aiSessionContextPluginKey, {
					type: 'RESET',
				});
			}

			return tr;
		},
	);

	return endSessionCommand(editorView.state, editorView.dispatch);
};

export const updateTargetId = ({
	editorView,
	targetId,
}: {
	editorView?: EditorView;
	targetId: string | number;
}): boolean | undefined => {
	if (!editorView) {
		return;
	}
	return createUpdateTargetIdCommand(targetId)(editorView.state, editorView.dispatch);
};

export const stageInlineRovoStreaming = ({
	editorView,
	targetId,
}: {
	editorView?: EditorView;
	targetId: string;
}): boolean | undefined => {
	if (!editorView) {
		return;
	}
	return createStageInlineRovoStreamingCommand(targetId)(editorView.state, editorView.dispatch);
};

interface GetPartialPMNodeFromSelectionOptions {
	maxSize?: number;
	selection: Selection | undefined;
}

export const getPartialPMNodeFromSelection: (
	options: GetPartialPMNodeFromSelectionOptions,
) => PMNode | undefined = ({ selection, maxSize = 10000 }) => {
	if (!selection) {
		return undefined;
	}

	const { $from, $to } = selection;

	const doc = $from.doc;

	/**
	 * Check the from and to positions of the lowest depth level (doc level).
	 * If the size is over the threshold, increase the depth.
	 */

	let depth = 0;

	while (
		// Check until max depth is reached from the resolved positions
		!isNaN($from.start(depth)) &&
		!isNaN($to.end(depth))
	) {
		const from = $from.start(depth);
		const to = $to.end(depth);
		if (to - from < maxSize) {
			break;
		}
		depth++;
	}

	if (depth === 0) {
		return doc;
	}

	// find a depth that fits and then start looking at siblings n - 1 and n + 1 and so on

	const parentDepth = depth - 1;
	const index = $from.index(parentDepth);

	let from = $from.start(depth);
	let to = $to.end(depth);
	let increment = 1;

	const maxIndex = $from.node(parentDepth).childCount;

	while (
		// Loop until both ends of the bounds have been reached
		index - increment >= 0 ||
		index + increment <= maxIndex
	) {
		const newFrom = $from.posAtIndex(Math.max(index - increment, 0), parentDepth);
		// We look ahead at the next index as posAtIndex yields the starting position of the node
		const newTo = $to.posAtIndex(Math.min(index + increment + 1, maxIndex), parentDepth);

		let isPositionUpdated = false;
		if (to - newFrom < maxSize) {
			from = newFrom;
			isPositionUpdated = true;
		}
		if (newTo - from < maxSize) {
			to = newTo;
			isPositionUpdated = true;
		}

		if (!isPositionUpdated) {
			break;
		}
		increment++;
	}

	return doc.cut(from, to);
};

export const getPartialDocumentFromSelection: (
	options: GetPartialPMNodeFromSelectionOptions,
) => DocNode | undefined = (options) => {
	const transformer = new JSONTransformer();
	const doc = getPartialPMNodeFromSelection(options);
	if (doc) {
		return transformer.encode(doc) as DocNode;
	}
};

export const getPartialMarkdownFromSelection: (
	options: GetPartialPMNodeFromSelectionOptions,
) => string | undefined = (options) => {
	const doc = getPartialPMNodeFromSelection(options);
	if (doc) {
		return convertProsemirrorNodeToMarkdown({ node: doc })?.markdown ?? '';
	}
};

export const tagAsAIStreamingTransformation = (
	tr: Transaction,
	options?: {
		isFinal: boolean;
	},
): Transaction => {
	if (options?.isFinal) {
		return tr.setMeta('isAIFinalStreamingTransformation', true);
	}
	return tr.setMeta('isAIStreamingTransformation', true);
};
